import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { StatusTone } from '@/components/status-badge';
import { Card } from '@/components/ui/card';
import { Item, ItemContent } from '@/components/ui/item';
import { cn } from '@/lib/utils';

/**
 * Número de destaque com o rótulo embaixo. `compact` é a versão de linha, ao lado de botões
 * (cabeçalho de um prompt); a normal vai numa `MetricGrid`.
 *
 * Na Visão geral (ADR-073) o número nunca vem sozinho: `badge` diz como ele está contra a
 * referência (acima do normal, +8,4% vs. 30 dias anteriores) e `to` leva para onde se investiga —
 * o painel não é beco sem saída.
 */
export function MetricCard({
  label,
  value,
  hint,
  badge,
  to,
  tone,
  compact = false,
  className,
}: {
  label: string;
  value: ReactNode;
  /** Contexto curto do número: período, base, fonte. */
  hint?: ReactNode;
  /** Situação contra a referência, ao lado do número. */
  badge?: ReactNode;
  /** Tela onde o número se abre. */
  to?: string;
  /**
   * Situação do indicador (ADR-074): faixa lateral e número na cor do tom, a mesma do
   * `StatusBadge`. Sem tom (ou `neutral`), o cartão fica como sempre — cor só onde significa algo.
   */
  tone?: StatusTone;
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    return (
      <Item variant="muted" size="sm" className={cn('px-3 py-1.5 text-right', className)}>
        <ItemContent className="gap-0">
          <div className="tabular text-sm font-semibold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </ItemContent>
      </Item>
    );
  }
  const body = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className={cn('tabular text-2xl font-semibold', tone && TONE_TEXT[tone])}>{value}</div>
        {badge}
      </div>
      <div className="text-xs text-muted-foreground">{label}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </>
  );
  const toned = tone && TONE_EDGE[tone];
  if (to)
    return (
      <Card className={cn('p-0 transition-colors hover:bg-accent/50', toned, className)}>
        <Link
          to={to}
          className="block rounded-xl p-4 outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          {body}
        </Link>
      </Card>
    );
  return <Card className={cn('p-4', toned, className)}>{body}</Card>;
}

/** Os mesmos tokens das variantes tingidas do `Badge` (contraste conferido nos dois temas). */
const TONE_EDGE: Record<StatusTone, string | undefined> = {
  success: 'border-l-4 border-l-success',
  warning: 'border-l-4 border-l-warning',
  danger: 'border-l-4 border-l-destructive',
  info: 'border-l-4 border-l-info',
  brand: 'border-l-4 border-l-brand',
  neutral: undefined,
};
const TONE_TEXT: Record<StatusTone, string | undefined> = {
  success: 'text-success-ink',
  warning: 'text-warning-ink',
  danger: 'text-danger-ink',
  info: 'text-info-ink',
  brand: undefined,
  neutral: undefined,
};

/** Grade de métricas: duas colunas no celular (uma só desperdiçaria a tela), quatro no desktop. */
export function MetricGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}>{children}</div>;
}
